/* global React */
import { Grid, lighten } from '@mui/material'
import { useMemoRef, useStyles } from '@platform/react/hook'
import Text from 'ui/Element/Text'
import { lazy, Suspense } from 'react'
import ErrorBoundary from 'ui/Error'
import SimpleButton from 'ui/Element/Button'

/**
 * Returns the Date element's current theme styles.
 *
 * @param {Object} theme  the application's current theme
 * @returns {Object}      the Date element's styles
 */
const styles = theme => ({
  root: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '& *': {
      marginRight: theme.spacing(1),
    },
  },
  message: {
    color: theme.palette.text.primary,
    fontWeight: 700,
    textAlign: 'center',
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  icon: {
    margin: 0,
  },
  ...theme.custom.systemMessage,
})

/**
 * Conversation helper function
 *
 * Returns a list of conversations for the current resource (e.g. ticket).
 *
 * @param {Object} classes          styles
 * @param {Object[]} conversations  list of recipients
 * @param {Object} Template         component for rendering each recipient
 * @returns {*}
 * @private
 */
const _conversations = ({
  conversations,
  Template,
}) => conversations.map((conversation, index) => {
  const recipient = `${conversation.firstName} ${conversation.lastName}`

  return (
    <Template
      key={index}
      fullWidth={false}
      value={recipient}
      variant={'contained'}
      color={'primary'}
      startIcon={{
        name: 'user',
      }}
      sx={{
        '& > .MuiButton-startIcon > div': {
          margin: 0,
        },
      }}
    />
  )
})

/**
 * Conversations
 *
 * Show a bar of currently ongoing conversations for the current ressource (e.g. ticket)
 *
 * TODO: Currently, only one recipient/conversation per ticket is allowed. Later on, we need this
 * component to function as a tab bar to switch between the different conversations.
 *
 * @param {Boolean} hidden                  whether to show the component
 * @param {Object} label                    the label
 * @param {Function: JSX.Element} template  the component to use for each conversation.
 * @param {Object} events                   incoming events
 * @param {Object} props                    additional props
 *
 * @returns {null|JSX.Element}
 */
const Conversations = ({ hidden = false, label, template = SimpleButton, events, ...props }) => {
  const { onOpen } = events
  const classes = useStyles(styles)()
  const Button = lazy(() => import(`@ui/${template}`))

  const { hidden: isEmpty, data: conversationList } = onOpen?.(null) || {}

  return hidden || isEmpty ? null : (
    <ErrorBoundary>
      <Suspense fallback={null}>
        <Grid
          item
          className={classes.root}
        >
          <Text
            key={'label'}
            className={classes.message}
            variant={'body1'}
          >
            {label}
          </Text>
          {_conversations({ classes, conversations: conversationList, Template: Button })}
        </Grid>
      </Suspense>
    </ErrorBoundary>
  )
}

export default useMemoRef(Conversations, props => [props.value])
